<!--
@component
StatsStat

See markup below.
-->

<script lang="ts">
  import type { components } from '$lib/@types/opta.api';
  import { onMount } from 'svelte';

  type StatDto = components['schemas']['StatDto'];

  interface Props {
    homeStat?: StatDto;
    awayStat?: StatDto;
    title: string;
    suffix?: string;
  }

  let { homeStat, awayStat, title, suffix = '' }: Props = $props();

  /** Parse a stat's `value` as a number, falling back to 0. */
  const toNumber = (stat?: StatDto): number => {
    const n = parseFloat(stat?.value ?? '');
    return Number.isFinite(n) ? n : 0;
  };

  const homeValue = $derived(toNumber(homeStat));
  const awayValue = $derived(toNumber(awayStat));

  /**
   * Width of the home team's bar as a percentage of the total. Returns 0
   * when both values are 0 (or missing) to avoid `NaN` from a 0/0 divide.
   */
  const targetWidth = $derived.by(() => {
    const total = homeValue + awayValue;
    if (total === 0) return 0;
    return Number(((homeValue / total) * 100).toPrecision(3));
  });

  /**
   * Animate the bar in once the component enters the viewport. We keep the
   * width at 0% until the element is visible, then transition to the
   * computed target width using the existing CSS transition.
   */
  let vizEl = $state<HTMLDivElement | undefined>();
  let visible = $state(false);

  const homeWidth = $derived(visible ? targetWidth : 0);

  onMount(() => {
    if (!vizEl) return;

    // Respect users who prefer reduced motion — show the final state.
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion || typeof IntersectionObserver === 'undefined') {
      visible = true;
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            visible = true;
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(vizEl);

    return () => observer.disconnect();
  });
</script>

{#if homeStat || awayStat}
  <div class="title text-center fmt-3 text-sm">{title}</div>
  <div class="stat flex justify-center items-center">
    <div class="value text-xs font-medium text-secondary">
      {homeValue}{suffix}
    </div>
    <div class="viz" bind:this={vizEl}>
      <div class="viz-foreground" style="width: {homeWidth}%;"></div>
      <div class="viz-background"></div>
    </div>
    <div class="value text-xs font-medium text-secondary">
      {awayValue}{suffix}
    </div>
  </div>
{/if}

<style lang="scss">
  @use 'sass:color';
  @use '../../../styles/mixins' as mixins;

  .title {
    @include mixins.font-light;
  }

  .viz {
    width: 100%;
    max-width: 21rem;
    margin: auto 0.35rem;
    height: 0.56rem;
    position: relative;
    opacity: 0.8;

    border-radius: var(--theme-style-border-radius);

    .viz-background {
      border-radius: var(--theme-style-border-radius);
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      // `color.scale()` is a Sass build-time function — it can't
      // resolve a CSS custom property. Keep the literal source colour
      // here (matches `--theme-colour-pitch-green`) so the tint is
      // computed at build time. If the brand pitch colour changes,
      // update this literal alongside the primitive in `theme.ts`.
      background-color: color.scale(
        #6aa86a,
        $lightness: 35%,
        $saturation: -10%
      );
      border: 1px solid var(--theme-colour-mid-grey);
    }
    .viz-foreground {
      background-color: var(--theme-colour-tan-grey);
      position: absolute;
      border-top-left-radius: var(--theme-style-border-radius);
      border-bottom-left-radius: var(--theme-style-border-radius);
      @include mixins.hard-shadow;
      height: 100%;
      top: 0;
      left: 0;
      z-index: 1;
      transition: width 1.1s cubic-bezier(0.22, 1, 0.36, 1);
      border: 1px solid var(--theme-colour-dark-grey);

      &.noBar {
        width: 0% !important;
      }
    }
  }
</style>
